import { create } from 'zustand';
import { createJSONStorage, devtools, persist } from 'zustand/middleware';
import { alertHookConfig } from './alertHookConfig';
import type { AlertHookParams, AlertHookType, CurrentAlertHook } from './types';

interface AlertHooksState {
  /** The hook currently being shown (suggestion or confirm), or null when nothing is open. */
  current: CurrentAlertHook | null;
  /** Hooks the user explicitly dismissed, keyed by `getDismissKey`. Persisted to localStorage. */
  dismissed: Record<string, true>;
  /**
   * Hook types activated in this session. Hides the in-page suggestions right away, before the
   * triggers list is fetched again with the new rule. Not persisted.
   */
  activated: Partial<Record<AlertHookType, true>>;

  /** Open a hook on step 1 (suggestion). Called by `useTriggerAlertHook` after its checks pass. */
  open: <T extends AlertHookType>(type: T, params: AlertHookParams<T>, onClosed?: () => void) => void;
  /** Move the open hook from step 1 (suggestion) to step 2 (preview + activation). */
  advanceToConfirm: () => void;
  /** Dismiss the open hook so it won't be suggested again for the same key, and close it. */
  dismiss: () => void;
  /** Close the open hook without dismissing it (activation, cancel, escape). */
  close: () => void;
  /** Dismiss a hook whose suggestion was closed outside the modal (banner, floating card). */
  dismissWithoutOpening: <T extends AlertHookType>(type: T, params: AlertHookParams<T>) => void;
  markActivated: (type: AlertHookType) => void;
}

const initialState: Pick<AlertHooksState, 'current' | 'dismissed' | 'activated'> = {
  current: null,
  dismissed: {},
  activated: {},
};

// localStorage may hold a corrupted/legacy payload — normalize so `dismissed` is always a record,
// otherwise `useTriggerAlertHook` would blow up indexing it.
const normalizePersisted = (persisted: unknown): Pick<AlertHooksState, 'dismissed'> => {
  const dismissed = (persisted as Partial<Pick<AlertHooksState, 'dismissed'>> | undefined)?.dismissed;
  return { dismissed: dismissed && typeof dismissed === 'object' && !Array.isArray(dismissed) ? dismissed : {} };
};

const useAlertHooksStore = create<AlertHooksState, [['zustand/devtools', never], ['zustand/persist', Pick<AlertHooksState, 'dismissed'>]]>(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,
        open: (type, params, onClosed) => set({ current: { type, params, step: 'suggestion', onClosed } }, false, 'open'),
        advanceToConfirm: () => set((state) => (state.current ? { current: { ...state.current, step: 'confirm' } } : state), false, 'advanceToConfirm'),
        dismiss: () => {
          const { current } = get();
          if (!current) return;
          const key = alertHookConfig[current.type].getDismissKey(current.params);
          set((state) => ({ current: null, dismissed: { ...state.dismissed, [key]: true } }), false, 'dismiss');
          current.onClosed?.();
        },
        close: () => {
          const { current } = get();
          if (!current) return;
          set({ current: null }, false, 'close');
          current.onClosed?.();
        },
        dismissWithoutOpening: (type, params) => {
          const key = alertHookConfig[type].getDismissKey(params);
          set((state) => ({ dismissed: { ...state.dismissed, [key]: true } }), false, 'dismissWithoutOpening');
        },
        markActivated: (type) => set((state) => ({ activated: { ...state.activated, [type]: true } }), false, 'markActivated'),
      }),
      {
        name: 'alertHooksDismissed',
        storage: createJSONStorage(() => localStorage),
        version: 1,
        // Only the dismissal record survives a reload — `current` is ephemeral UI state.
        partialize: (state) => ({ dismissed: state.dismissed }),
        // `migrate` only runs on version mismatch, so `merge` normalizes same-version payloads too.
        migrate: normalizePersisted,
        merge: (persisted, current) => ({ ...current, ...normalizePersisted(persisted) }),
      }
    ),
    { name: 'useAlertHooksStore' }
  )
);

export type { AlertHooksState };
export default useAlertHooksStore;
